import React, { useEffect, useState } from 'react';
import {
  Button,
  ComposedModal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelect,
  NumberInput,
  Select,
  SelectItem,
  TextArea,
  TextInput,
  InlineNotification,
} from '@carbon/react';

import {
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
} from '@/redux/new_services/holidays';
import { useGetFederalStatesQuery } from '@/redux/new_services/federalStates';

import HolidayVariantsEditor from './HolidayVariantsEditor';

// Recurrence type options shown in the form select. The labels are German
// admin-facing strings; the values must match the API constants.
const RECURRENCE_TYPES = [
  { value: 'fixed', label: 'Festes Datum (Tag/Monat)' },
  { value: 'easter_relative', label: 'Relativ zu Ostern' },
  { value: 'advent_relative', label: 'Relativ zum 1. Advent' },
  { value: 'buss_und_bettag', label: 'Buß- und Bettag (automatisch)' },
];

// Empty form scaffold — used when creating a new holiday.
const blank = () => ({
  name: '',
  description: '',
  recurrence_type: 'fixed',
  recurrence_data: { month: 1, day: 1 },
  federal_states: [],
});

// Convert a holiday record from the API (with its raw recurrence_data string)
// into the shape used by the form's local state.
const fromApi = (h) => ({
  name: h.name || '',
  description: h.description || '',
  recurrence_type: h.recurrence_type || 'fixed',
  recurrence_data: h.recurrence_data ? safeParse(h.recurrence_data) : {},
  federal_states: h.federal_states || [],
});

const safeParse = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return {};
  }
};

// HolidayForm hosts the create + edit modal. Accepts either a holiday to
// edit or null to render an empty form.
const HolidayForm = ({ open, holiday, onClose }) => {
  const [state, setState] = useState(blank());
  const [error, setError] = useState(null);

  const { data: federalStates = [] } = useGetFederalStatesQuery();
  const [createHoliday, { isLoading: isCreating }] = useCreateHolidayMutation();
  const [updateHoliday, { isLoading: isUpdating }] = useUpdateHolidayMutation();
  const isEdit = Boolean(holiday?.model?.ID);
  const isSaving = isCreating || isUpdating;

  // Re-seed local state every time the modal is (re)opened so previous edits
  // don't leak between sessions.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setState(holiday ? fromApi(holiday) : blank());
  }, [open, holiday]);

  const setField = (key, value) => setState((s) => ({ ...s, [key]: value }));

  const setRecurrenceType = (type) => {
    // Reset payload to a sensible default for the chosen type so the
    // dependent input fields can render without `undefined`.
    let data = {};
    if (type === 'fixed') data = { month: 1, day: 1 };
    if (type === 'easter_relative') data = { offset_days: 0 };
    if (type === 'advent_relative') data = { offset_days: 0 };
    setState((s) => ({ ...s, recurrence_type: type, recurrence_data: data }));
  };

  const setRecurrenceField = (key, value) => {
    setState((s) => ({
      ...s,
      recurrence_data: { ...s.recurrence_data, [key]: value },
    }));
  };

  const handleSave = async () => {
    if (!state.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    const payload = {
      name: state.name.trim(),
      description: state.description,
      recurrence_type: state.recurrence_type,
      recurrence_data:
        state.recurrence_type === 'buss_und_bettag'
          ? ''
          : JSON.stringify(state.recurrence_data),
      federal_states: state.federal_states,
    };
    try {
      if (isEdit) {
        await updateHoliday({ id: holiday.model.ID, ...payload }).unwrap();
      } else {
        await createHoliday(payload).unwrap();
      }
      onClose?.();
    } catch (e) {
      setError(e?.data?.message || 'Speichern fehlgeschlagen');
    }
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="lg">
      <ModalHeader title={isEdit ? 'Feiertag bearbeiten' : 'Neuer Feiertag'} />
      <ModalBody hasForm>
        <Form>
          {error && (
            <InlineNotification
              kind="error"
              title="Fehler"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
              lowContrast
            />
          )}

          <TextInput
            id="holiday-name"
            labelText="Name"
            value={state.name}
            onChange={(e) => setField('name', e.target.value)}
            required
          />
          <TextArea
            id="holiday-description"
            labelText="Beschreibung"
            value={state.description}
            onChange={(e) => setField('description', e.target.value)}
            rows={2}
          />

          <Select
            id="holiday-recurrence-type"
            labelText="Wiederholungstyp"
            value={state.recurrence_type}
            onChange={(e) => setRecurrenceType(e.target.value)}
          >
            {RECURRENCE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} text={t.label} />
            ))}
          </Select>

          {state.recurrence_type === 'fixed' && (
            <div style={{ display: 'flex', gap: '1rem' }}>
              <NumberInput
                id="holiday-month"
                label="Monat (1-12)"
                min={1}
                max={12}
                value={state.recurrence_data.month ?? 1}
                onChange={(_e, { value }) => setRecurrenceField('month', Number(value))}
              />
              <NumberInput
                id="holiday-day"
                label="Tag (1-31)"
                min={1}
                max={31}
                value={state.recurrence_data.day ?? 1}
                onChange={(_e, { value }) => setRecurrenceField('day', Number(value))}
              />
            </div>
          )}

          {state.recurrence_type === 'easter_relative' && (
            <NumberInput
              id="holiday-easter-offset"
              label="Tage relativ zu Ostersonntag (Karfreitag = -2, Ostermontag = +1, Christi Himmelfahrt = +39, Pfingstmontag = +50, Fronleichnam = +60)"
              value={state.recurrence_data.offset_days ?? 0}
              onChange={(_e, { value }) => setRecurrenceField('offset_days', Number(value))}
            />
          )}

          {state.recurrence_type === 'advent_relative' && (
            <NumberInput
              id="holiday-advent-offset"
              label="Tage relativ zum 1. Advent (Volkstrauertag = -7, Totensonntag = 0)"
              value={state.recurrence_data.offset_days ?? 0}
              onChange={(_e, { value }) => setRecurrenceField('offset_days', Number(value))}
            />
          )}

          {state.recurrence_type === 'buss_und_bettag' && (
            <InlineNotification
              kind="info"
              title="Hinweis"
              subtitle="Wird automatisch auf den Mittwoch vor dem 23. November berechnet."
              hideCloseButton
              lowContrast
            />
          )}

          <MultiSelect
            id="holiday-federal-states"
            titleText="Bundesländer"
            label="Bundesländer wählen"
            items={federalStates}
            itemToString={(item) => (item ? item.name : '')}
            initialSelectedItems={state.federal_states}
            selectedItems={state.federal_states}
            onChange={({ selectedItems }) => setField('federal_states', selectedItems)}
          />

          {isEdit && <HolidayVariantsEditor holidayId={holiday.model.ID} />}
        </Form>
      </ModalBody>
      <ModalFooter
        primaryButtonText={isSaving ? 'Speichern …' : 'Speichern'}
        primaryButtonDisabled={isSaving}
        secondaryButtonText="Abbrechen"
        onRequestClose={onClose}
        onRequestSubmit={handleSave}
      />
    </ComposedModal>
  );
};

export default HolidayForm;
